import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { IDossier } from '../components/widgets/types';

export interface JuridicalReference {
    id: string;
    type: 'jurisprudence' | 'legifrance' | 'hal';
    text: string;
    blockId: string;
    chapterIndex: number;
    chapterTitle: string;
    details: {
        [key: string]: any;
    };
}

interface JuridicalReferencesContextType {
    references: JuridicalReference[];
    totalReferences: number;
    updateReferences: (dossierContent: IDossier | null) => void;
    addReference: (reference: JuridicalReference) => void;
    removeReference: (referenceId: string) => void;
}

const JuridicalReferencesContext = createContext<JuridicalReferencesContextType>({
    references: [],
    totalReferences: 0,
    updateReferences: () => { },
    addReference: () => { },
    removeReference: () => { },
});

export const useJuridicalReferences = () => useContext(JuridicalReferencesContext);

interface JuridicalReferencesProviderProps {
    children: ReactNode;
    initialDossier: IDossier | null;
}

/**
 * Sanitise le contenu HTML pour éviter les attaques XSS
 * Supprime les scripts, événements JavaScript et URLs javascript:
 */
const sanitizeHTML = (html: string): string => {
    if (!html) return '';

    // Supprimer les balises script complètement
    let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    // Supprimer les attributs d'événements JavaScript (onclick, onload, etc.)
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');

    // Supprimer les URLs javascript:
    sanitized = sanitized.replace(/javascript\s*:/gi, '');

    // Supprimer les balises potentiellement dangereuses
    const dangerousTags = ['script', 'object', 'embed', 'link', 'style', 'meta', 'iframe', 'frame', 'frameset'];
    dangerousTags.forEach(tag => {
        const regex = new RegExp(`<\\/?${tag}\\b[^>]*>`, 'gi');
        sanitized = sanitized.replace(regex, '');
    });

    // Supprimer les attributs style avec expression() ou -moz-binding
    sanitized = sanitized.replace(/style\s*=\s*["'][^"']*(?:expression|moz-binding)[^"']*["']/gi, '');

    return sanitized;
};

/**
 * Valide les attributs extraits pour éviter l'injection de code
 */
const validateAttribute = (value: string | null): string | null => {
    if (!value) return null;

    // Supprimer les caractères potentiellement dangereux
    const cleaned = value.replace(/[<>'"&]/g, '');

    // Vérifier que c'est bien un identifiant alphanumérique avec tirets/underscores
    if (!/^[a-zA-Z0-9_-]+$/.test(cleaned)) {
        console.warn(`Attribut invalide détecté et ignoré: ${value}`);
        return null;
    }

    return cleaned;
};

export const JuridicalReferencesProvider: React.FC<JuridicalReferencesProviderProps> = ({
    children,
    initialDossier
}) => {
    const [references, setReferences] = useState<JuridicalReference[]>([]);
    const [lastProcessedDossierId, setLastProcessedDossierId] = useState<string | null>(null);

    // Fonction pour extraire les références juridiques du HTML sécurisé
    const extractReferencesFromHTML = (html: string, blockId: string, chapterIndex: number, chapterTitle: string): JuridicalReference[] => {
        // SÉCURISATION: Sanitiser le HTML avant parsing
        const sanitizedHTML = sanitizeHTML(html);

        const parser = new DOMParser();
        const doc = parser.parseFromString(sanitizedHTML, 'text/html');
        const extractedReferences: JuridicalReference[] = [];

        // Extraire les références de jurisprudence
        const jurisprudenceElements = doc.querySelectorAll('.jurisprudence-highlight');
        jurisprudenceElements.forEach((element) => {
            const referenceId = validateAttribute(element.getAttribute('data-reference-id'));
            if (referenceId) {
                extractedReferences.push({
                    id: referenceId,
                    type: 'jurisprudence',
                    text: element.textContent || '',
                    blockId,
                    chapterIndex,
                    chapterTitle,
                    details: {
                        referenceId
                    }
                });
            }
        });

        // Extraire les références Legifrance
        const legifranceElements = doc.querySelectorAll('.legifrance-highlight');
        legifranceElements.forEach((element) => {
            const codeId = validateAttribute(element.getAttribute('data-legifrance-code'));
            const sectionId = validateAttribute(element.getAttribute('data-legifrance-section'));
            const articleId = validateAttribute(element.getAttribute('data-legifrance-article'));

            if (codeId) {
                const refId = `${codeId}-${sectionId || ''}-${articleId || ''}`;
                extractedReferences.push({
                    id: refId,
                    type: 'legifrance',
                    text: element.textContent || '',
                    blockId,
                    chapterIndex,
                    chapterTitle,
                    details: {
                        codeId,
                        sectionId: sectionId || undefined,
                        articleId: articleId || undefined
                    }
                });
            }
        });

        // Extraire les références HAL
        const halElements = doc.querySelectorAll('.hal-highlight');
        halElements.forEach((element) => {
            const halId = validateAttribute(element.getAttribute('data-hal-id'));
            const halTitle = element.getAttribute('data-hal-title');

            if (halId) {
                extractedReferences.push({
                    id: halId,
                    type: 'hal',
                    text: element.textContent || '',
                    blockId,
                    chapterIndex,
                    chapterTitle,
                    details: {
                        halId,
                        halTitle
                    }
                });
            }
        });

        return extractedReferences;
    };

    // Fonction pour mettre à jour les références à partir du contenu du dossier
    // Mémoriser cette fonction avec useCallback pour éviter les recréations inutiles
    const updateReferences = useCallback((dossier: IDossier | null) => {
        if (!dossier) {
            setReferences([]);
            return;
        }

        // Vérifier si c'est le même dossier que celui déjà traité
        // Si oui, on évite une mise à jour inutile
        if (lastProcessedDossierId === dossier._id) {
            return;
        }

        const allReferences: JuridicalReference[] = [];

        // Parcourir tous les chapitres, sections et blocs.
        // Le `?? []` protège les dossiers historiques où certaines sections
        // n'ont pas de champ `blocks` (avant l'ajout du default Mongoose)
        // ou ont des refs Block orphelines populée à null.
        (dossier.content ?? []).forEach((chapter, chapterIndex) => {
            const chapterTitle = chapter.title || `Chapitre ${chapterIndex + 1}`;

            (chapter.sections ?? []).forEach(section => {
                (section.blocks ?? []).forEach(block => {
                    // Vérifier si c'est un bloc de texte composite
                    if (block.type === 'compositeText') {
                        const compositeTextBlock = block as any; // Utiliser any pour contourner les erreurs de typage

                        // Parcourir tous les sous-blocs de texte
                        if (compositeTextBlock.content && compositeTextBlock.content.blocks) {
                            compositeTextBlock.content.blocks.forEach((textBlock: any) => {
                                // Extraire les références du HTML brut SÉCURISÉ
                                if (textBlock.rawText) {
                                    try {
                                        const blockReferences = extractReferencesFromHTML(
                                            textBlock.rawText,
                                            block._id,
                                            chapterIndex,
                                            chapterTitle
                                        );
                                        allReferences.push(...blockReferences);
                                    } catch (error) {
                                        console.error('Erreur lors de l\'extraction des références:', error);
                                        // Continue le traitement même en cas d'erreur sur un bloc
                                    }
                                }
                            });
                        }
                    }
                });
            });
        });

        // Mettre à jour l'état avec toutes les références trouvées
        setReferences(allReferences);
        // Mettre à jour l'ID du dernier dossier traité
        setLastProcessedDossierId(dossier._id);
    }, [lastProcessedDossierId]); // Dépendance seulement à lastProcessedDossierId

    // Initialiser les références au chargement du dossier
    useEffect(() => {
        if (initialDossier && initialDossier._id !== lastProcessedDossierId) {
            updateReferences(initialDossier);
        }
        // Dépendance explicite à initialDossier._id plutôt qu'à l'objet complet
    }, [initialDossier?._id, lastProcessedDossierId, updateReferences]);

    // Fonction pour ajouter une référence
    const addReference = useCallback((reference: JuridicalReference) => {
        setReferences(prev => {
            // Vérifier si la référence existe déjà
            const existingIndex = prev.findIndex(ref => ref.id === reference.id);
            if (existingIndex >= 0) {
                // Mettre à jour la référence existante si elle est différente
                if (JSON.stringify(prev[existingIndex]) !== JSON.stringify(reference)) {
                    const updated = [...prev];
                    updated[existingIndex] = reference;
                    return updated;
                }
                return prev; // Pas de mise à jour si identique
            } else {
                // Ajouter la nouvelle référence
                return [...prev, reference];
            }
        });
    }, []);

    // Fonction pour supprimer une référence
    const removeReference = useCallback((referenceId: string) => {
        setReferences(prev => prev.filter(ref => ref.id !== referenceId));
    }, []);

    return (
        <JuridicalReferencesContext.Provider
            value={{
                references,
                totalReferences: references.length,
                updateReferences,
                addReference,
                removeReference
            }}
        >
            {children}
        </JuridicalReferencesContext.Provider>
    );
};